import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as Sentry from '@sentry/browser';
import { createLibraryPlanTemplate, updateLibraryPlanTemplate } from '../../graphql/resourceLibraryQueries';
import { LibraryPlanTemplate, LibraryPlanCategory, LibraryCategoryTopicRef } from '../../types/resourceLibraryTypes';
import { TopicPoolPickerModal } from './TopicPoolPickerModal';
import { CustomModal } from '../CustomModal';
import { Btn, BtnIconOnly, IconAngleDown, IconAngleUp, IconClose, IconInfo, IconPlus, InputField, Select } from '../ui';
import '../../css/ResourceLibraryContent.css';

interface PlanTemplateEditorModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
    editingPlan: LibraryPlanTemplate | null;
    adminId: string;
    adminName: string;
}

const STANDARDS = [
    { label: 'Select...', value: '' },
    { label: 'MSHA', value: 'MSHA' },
    { label: 'OSHA', value: 'OSHA' },
    { label: 'General', value: 'General' },
    { label: 'Custom', value: 'Custom' },
];

function generateId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `cat-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function parseCategories(json: string): LibraryPlanCategory[] {
    try {
        return JSON.parse(json) as LibraryPlanCategory[];
    } catch {
        return [];
    }
}

export const PlanTemplateEditorModal: React.FC<PlanTemplateEditorModalProps> = ({
    isOpen,
    onClose,
    onSave,
    editingPlan,
    adminId,
    adminName,
}) => {
    // --- Form state ---
    const [name, setName] = useState('');
    const [standard, setStandard] = useState('');
    const [durationDays, setDurationDays] = useState<number | ''>('');
    const [minTimeRequired, setMinTimeRequired] = useState<number | ''>('');
    const [categories, setCategories] = useState<LibraryPlanCategory[]>([]);
    const [status, setStatus] = useState<'DRAFT' | 'ACTIVE'>('DRAFT');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Category expansion state
    const [expandedCats, setExpandedCats] = useState<Set<string>>(new Set());

    // Topic pool picker state
    const [pickerCategoryId, setPickerCategoryId] = useState<string | null>(null);
    const pickerCategory = categories.find((c) => c.id === pickerCategoryId) || null;

    // --- Pre-populate when editing ---
    useEffect(() => {
        if (!isOpen) return;
        if (editingPlan) {
            setName(editingPlan.name);
            setStandard(editingPlan.governingStandard);
            setDurationDays(editingPlan.cycleDuration);
            setMinTimeRequired(editingPlan.minimumTimeRequired);
            const parsed = parseCategories(editingPlan.categories);
            setCategories(parsed);
            setStatus(editingPlan.status === 'ACTIVE' ? 'ACTIVE' : 'DRAFT');
            setExpandedCats(new Set(parsed.map((c) => c.id)));
        } else {
            setName('');
            setStandard('');
            setDurationDays('');
            setMinTimeRequired('');
            setCategories([]);
            setStatus('DRAFT');
            setExpandedCats(new Set());
        }
        setError(null);
    }, [isOpen, editingPlan]);

    // --- Computed values ---
    const totalTopics = useMemo(() => categories.reduce((sum, c) => sum + c.topicRefs.length, 0), [categories]);

    const totalScheduledMinutes = useMemo(
        () => categories.reduce((sum, c) => sum + c.topicRefs.reduce((s, t) => s + t.timeValue, 0), 0),
        [categories],
    );

    const daysCovered = useMemo(
        () => totalTopics, // each topic = 1 day
        [totalTopics],
    );

    // --- Category handlers ---
    const handleAddCategory = () => {
        const newCat: LibraryPlanCategory = {
            id: generateId(),
            name: '',
            targetMinutes: 0,
            topicRefs: [],
        };
        setCategories((prev) => [...prev, newCat]);
        setExpandedCats((prev) => new Set([...prev, newCat.id]));
    };

    const handleCategoryNameChange = (catId: string, newName: string) => {
        setCategories((prev) => prev.map((c) => (c.id === catId ? { ...c, name: newName } : c)));
    };

    const handleCategoryTargetChange = (catId: string, target: number) => {
        setCategories((prev) => prev.map((c) => (c.id === catId ? { ...c, targetMinutes: target } : c)));
    };

    const handleRemoveCategory = (catId: string) => {
        setCategories((prev) => prev.filter((c) => c.id !== catId));
    };

    const toggleCategoryExpand = (catId: string) => {
        setExpandedCats((prev) => {
            const next = new Set(prev);
            if (next.has(catId)) next.delete(catId);
            else next.add(catId);
            return next;
        });
    };

    // --- Topic handlers ---
    const handleRemoveTopic = (catId: string, sortOrder: number) => {
        setCategories((prev) =>
            prev.map((c) => {
                if (c.id !== catId) return c;
                const filtered = c.topicRefs
                    .filter((t) => t.sortOrder !== sortOrder)
                    .map((t, i) => ({ ...t, sortOrder: i + 1 }));
                return { ...c, topicRefs: filtered };
            }),
        );
    };

    const handleMoveTopicUp = (catId: string, sortOrder: number) => {
        if (sortOrder <= 1) return;
        setCategories((prev) =>
            prev.map((c) => {
                if (c.id !== catId) return c;
                const refs = [...c.topicRefs].sort((a, b) => a.sortOrder - b.sortOrder);
                const idx = refs.findIndex((t) => t.sortOrder === sortOrder);
                if (idx <= 0) return c;
                [refs[idx - 1], refs[idx]] = [refs[idx], refs[idx - 1]];
                return { ...c, topicRefs: refs.map((t, i) => ({ ...t, sortOrder: i + 1 })) };
            }),
        );
    };

    const handleMoveTopicDown = (catId: string, sortOrder: number) => {
        setCategories((prev) =>
            prev.map((c) => {
                if (c.id !== catId) return c;
                const refs = [...c.topicRefs].sort((a, b) => a.sortOrder - b.sortOrder);
                const idx = refs.findIndex((t) => t.sortOrder === sortOrder);
                if (idx < 0 || idx >= refs.length - 1) return c;
                [refs[idx], refs[idx + 1]] = [refs[idx + 1], refs[idx]];
                return { ...c, topicRefs: refs.map((t, i) => ({ ...t, sortOrder: i + 1 })) };
            }),
        );
    };

    // --- Topic pool picker ---
    const handleOpenPicker = (catId: string) => {
        setPickerCategoryId(catId);
    };

    const handlePickerClose = () => {
        setPickerCategoryId(null);
    };

    const handleTopicsAdded = useCallback(
        (newTopics: LibraryCategoryTopicRef[]) => {
            if (!pickerCategoryId) return;
            setCategories((prev) =>
                prev.map((c) => {
                    if (c.id !== pickerCategoryId) return c;
                    const existingMax = c.topicRefs.length > 0 ? Math.max(...c.topicRefs.map((t) => t.sortOrder)) : 0;
                    const appended = newTopics.map((t, i) => ({
                        ...t,
                        sortOrder: existingMax + i + 1,
                    }));
                    return { ...c, topicRefs: [...c.topicRefs, ...appended] };
                }),
            );
            setPickerCategoryId(null);
        },
        [pickerCategoryId],
    );

    // --- Save handler ---
    const handleSave = async (saveStatus: 'DRAFT' | 'ACTIVE') => {
        if (!name.trim() || !standard || !durationDays || !minTimeRequired) {
            setError('Please fill in all required fields.');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const categoriesJson = JSON.stringify(categories);
            const topicCount = categories.reduce((s, c) => s + c.topicRefs.length, 0);
            const categoryCount = categories.length;

            if (editingPlan) {
                await updateLibraryPlanTemplate({
                    id: editingPlan.id,
                    name: name.trim(),
                    governingStandard: standard,
                    cycleDuration: Number(durationDays),
                    minimumTimeRequired: Number(minTimeRequired),
                    categories: categoriesJson,
                    workingDays: [1, 2, 3, 4, 5],
                    status: saveStatus,
                    topicCount,
                    categoryCount,
                    createdByAdminId: adminId,
                    createdByAdminName: adminName,
                });
            } else {
                await createLibraryPlanTemplate({
                    name: name.trim(),
                    governingStandard: standard,
                    cycleDuration: Number(durationDays),
                    minimumTimeRequired: Number(minTimeRequired),
                    categories: categoriesJson,
                    workingDays: [1, 2, 3, 4, 5],
                    status: saveStatus,
                    topicCount,
                    categoryCount,
                    createdByAdminId: adminId,
                    createdByAdminName: adminName,
                });
            }
            onSave();
        } catch (err) {
            console.error('Error saving plan template:', err);
            Sentry.captureException(err);
            setError('Failed to save. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    // --- Validation ---
    const canPublish = !!(name.trim() && standard && durationDays && minTimeRequired && totalTopics > 0);

    if (!isOpen) return null;

    // --- Compute day numbers for display ---
    let runningDay = 0;
    const categoryDayMap: Record<string, number[]> = {};
    for (const cat of categories) {
        const days: number[] = [];
        const sorted = [...cat.topicRefs].sort((a, b) => a.sortOrder - b.sortOrder);
        for (let i = 0; i < sorted.length; i++) {
            runningDay++;
            days.push(runningDay);
        }
        categoryDayMap[cat.id] = days;
    }

    return (
        <>
            <CustomModal
                visible
                onDismiss={onClose}
                size="xlarge"
                header={editingPlan ? `Edit Plan Template — ${name}` : 'Create Plan Template'}
                subHeader="Topics are sequenced here. Each topic = 1 day in the customer's cycle, starting from their enrollment date."
                bodyClassName="zrl-dialog-body zrl-editor-body"
                footer={
                    <div className="modal-footer-actions">
                        <Btn buttonSize="sm" variant="secondary-outlined" buttonText="Cancel" buttonOnClick={onClose} />
                        <Btn
                            buttonSize="sm"
                            variant="secondary-outlined"
                            buttonText={saving ? 'Saving...' : 'Save as Draft'}
                            buttonDisabled={saving || !name.trim()}
                            buttonOnClick={() => handleSave('DRAFT')}
                        />
                        <Btn
                            buttonSize="sm"
                            variant="primary"
                            buttonText={saving ? 'Saving...' : 'Save & Publish'}
                            buttonDisabled={saving || !canPublish}
                            buttonOnClick={() => handleSave('ACTIVE')}
                        />
                    </div>
                }
            >
                {/* Header Fields Row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16 }}>
                    <div className="zrl-form-group">
                        <label htmlFor="zrl-input-name" className="zrl-form-label">
                            Template Name <span>*</span>
                        </label>
                        <InputField
                            inputId="zrl-input-name"
                            placeholder="e.g. MSHA Annual Plan"
                            value={name}
                            onChange={(value) => setName(value)}
                        />
                    </div>
                    <div className="zrl-form-group">
                        <label htmlFor="zrl-input-standard" className="zrl-form-label">
                            Standard <span>*</span>
                        </label>
                        <Select
                            inputId="zrl-input-standard"
                            placeholder="Select..."
                            options={STANDARDS}
                            value={standard}
                            onChange={(value) => setStandard(value)}
                        />
                    </div>
                    <div className="zrl-form-group">
                        <label htmlFor="zrl-input-duration" className="zrl-form-label">
                            Duration Days <span>*</span>
                        </label>
                        <InputField
                            inputId="zrl-input-duration"
                            type="number"
                            min={1}
                            placeholder="e.g. 365"
                            value={String(durationDays)}
                            onChange={(value) => setDurationDays(value ? Number(value) : '')}
                        />
                    </div>
                    <div className="zrl-form-group">
                        <label htmlFor="zrl-input-time-required" className="zrl-form-label">
                            Min Time Required (min) <span>*</span>
                        </label>
                        <InputField
                            inputId="zrl-input-time-required"
                            type="number"
                            min={0}
                            placeholder="e.g. 480"
                            value={String(minTimeRequired)}
                            onChange={(value) => setMinTimeRequired(value ? Number(value) : '')}
                        />
                    </div>
                </div>

                {/* Info Note */}
                <div className="zrl-info-banner zrl-info-banner-warn" style={{ marginBottom: 20 }}>
                    <span className="zrl-info-banner-icon">
                        <IconInfo />
                    </span>
                    <span>
                        Day numbers are assigned automatically based on topic order. Drag topics within a category to
                        reorder. Day 1 = the customer's enrollment date.
                    </span>
                </div>

                {/* Two-Panel Layout */}
                <div className="zrl-two-panel">
                    {/* LEFT PANEL — Categories & Topic Sequence */}
                    <div className="zrl-panel-left">
                        <h3 className="zrl-panel-header">Categories &amp; Topic Sequence</h3>

                        {categories.length === 0 && (
                            <div className="zrl-empty" style={{ padding: '24px 0' }}>
                                No categories yet. Add one to start building the plan.
                            </div>
                        )}

                        {categories.map((cat) => {
                            const isExpanded = expandedCats.has(cat.id);
                            const sortedRefs = [...cat.topicRefs].sort((a, b) => a.sortOrder - b.sortOrder);
                            const scheduledMinutes = cat.topicRefs.reduce((s, t) => s + t.timeValue, 0);
                            const days = categoryDayMap[cat.id] || [];

                            return (
                                <div
                                    key={cat.id}
                                    style={{
                                        border: '1px solid #e8ecef',
                                        borderRadius: 10,
                                        marginBottom: 12,
                                        overflow: 'hidden',
                                    }}
                                >
                                    {/* Category Header */}
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 10,
                                            padding: '10px 14px',
                                            background: '#f7f9fb',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => toggleCategoryExpand(cat.id)}
                                    >
                                        <span style={{ fontSize: '0.8rem', color: 'var(--slate-gray)' }}>
                                            {isExpanded ? '▼' : '▶'}
                                        </span>
                                        <div className="zrl-category-name" onClick={(e) => e.stopPropagation()}>
                                            <InputField
                                                ariaLabel="Category name"
                                                placeholder="Category name..."
                                                value={cat.name}
                                                onChange={(value) => handleCategoryNameChange(cat.id, value)}
                                            />
                                        </div>
                                        <span
                                            style={{
                                                fontSize: '0.75rem',
                                                color: 'var(--slate-gray)',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {cat.topicRefs.length} topics
                                        </span>
                                        <span
                                            style={{
                                                fontSize: '0.75rem',
                                                color: 'var(--slate-gray)',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            Target:
                                        </span>
                                        <div className="zrl-category-target" onClick={(e) => e.stopPropagation()}>
                                            <InputField
                                                ariaLabel="Category target minutes"
                                                type="number"
                                                min={0}
                                                value={String(cat.targetMinutes || '')}
                                                onChange={(value) =>
                                                    handleCategoryTargetChange(cat.id, Number(value) || 0)
                                                }
                                            />
                                        </div>
                                        <span
                                            style={{
                                                fontSize: '0.75rem',
                                                color: 'var(--slate-gray)',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            min | Sched: {scheduledMinutes} min
                                        </span>
                                        <BtnIconOnly
                                            size="sm"
                                            variant="danger"
                                            icon={<IconClose />}
                                            ariaLabel="Remove category"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveCategory(cat.id);
                                            }}
                                        />
                                    </div>

                                    {/* Expanded: Topic List */}
                                    {isExpanded && (
                                        <div style={{ padding: '10px 14px' }}>
                                            {sortedRefs.length === 0 ? (
                                                <div
                                                    style={{
                                                        fontSize: '0.82rem',
                                                        color: 'var(--slate-gray)',
                                                        padding: '8px 0',
                                                    }}
                                                >
                                                    No topics in this category yet.
                                                </div>
                                            ) : (
                                                <table className="zrl-table" style={{ marginTop: 0 }}>
                                                    <thead>
                                                        <tr>
                                                            <th>Day</th>
                                                            <th>Topic ID</th>
                                                            <th>Style</th>
                                                            <th>Description</th>
                                                            <th>Time</th>
                                                            <th>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {sortedRefs.map((ref, idx) => (
                                                            <tr key={`${ref.topicId}-${ref.sortOrder}`}>
                                                                <td>{days[idx] ?? '—'}</td>
                                                                <td
                                                                    style={{
                                                                        fontFamily: 'monospace',
                                                                        fontSize: '0.78rem',
                                                                    }}
                                                                >
                                                                    {ref.topicId}
                                                                </td>
                                                                <td>
                                                                    <span
                                                                        className="zrl-badge zrl-badge-style"
                                                                        style={{
                                                                            background:
                                                                                ref.style === 'Video'
                                                                                    ? '#e8edf8'
                                                                                    : ref.style === 'PDF'
                                                                                      ? '#fde8e8'
                                                                                      : ref.style === 'Poster'
                                                                                        ? '#fef3cd'
                                                                                        : '#e8f8ef',
                                                                            color:
                                                                                ref.style === 'Video'
                                                                                    ? '#2c5282'
                                                                                    : ref.style === 'PDF'
                                                                                      ? '#c0392b'
                                                                                      : ref.style === 'Poster'
                                                                                        ? '#856404'
                                                                                        : '#1a8a4a',
                                                                        }}
                                                                    >
                                                                        {ref.style}
                                                                    </span>
                                                                </td>
                                                                <td style={{ fontSize: '0.82rem' }}>
                                                                    {ref.description}
                                                                </td>
                                                                <td>{ref.timeValue} min</td>
                                                                <td>
                                                                    <div className="zrl-actions">
                                                                        <BtnIconOnly
                                                                            size="sm"
                                                                            icon={<IconAngleUp />}
                                                                            ariaLabel="Move topic up"
                                                                            disabled={ref.sortOrder <= 1}
                                                                            onClick={() =>
                                                                                handleMoveTopicUp(cat.id, ref.sortOrder)
                                                                            }
                                                                        />
                                                                        <BtnIconOnly
                                                                            size="sm"
                                                                            icon={<IconAngleDown />}
                                                                            ariaLabel="Move topic down"
                                                                            disabled={
                                                                                ref.sortOrder >= sortedRefs.length
                                                                            }
                                                                            onClick={() =>
                                                                                handleMoveTopicDown(
                                                                                    cat.id,
                                                                                    ref.sortOrder,
                                                                                )
                                                                            }
                                                                        />
                                                                        <BtnIconOnly
                                                                            size="sm"
                                                                            variant="danger"
                                                                            icon={<IconClose />}
                                                                            ariaLabel="Remove topic"
                                                                            onClick={() =>
                                                                                handleRemoveTopic(cat.id, ref.sortOrder)
                                                                            }
                                                                        />
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            )}
                                            <Btn
                                                variant="secondary-outlined"
                                                buttonSize="sm"
                                                buttonIcon={<IconPlus iconClasses="zrl-inline-action-icon" />}
                                                buttonText="Add topic from pool"
                                                buttonClasses="zrl-inline-action"
                                                buttonOnClick={() => handleOpenPicker(cat.id)}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        <Btn
                            variant="secondary-outlined"
                            buttonSize="sm"
                            buttonIcon={<IconPlus iconClasses="zrl-inline-action-icon" />}
                            buttonText="Add Category"
                            buttonClasses="zrl-inline-action zrl-inline-action-warn"
                            buttonOnClick={handleAddCategory}
                        />
                    </div>

                    {/* RIGHT PANEL — Summary & Status */}
                    <div className="zrl-panel-right">
                        {/* Plan Summary Card */}
                        <div
                            style={{
                                background: '#f7f9fb',
                                borderRadius: 10,
                                marginBottom: 16,
                            }}
                        >
                            <h4 className="zrl-panel-header">Plan Summary</h4>
                            <div className="zrl-panel-summary">
                                <div className="zrl-panel-summary-item">
                                    <span className="zrl-panel-summary-title">Total Topics</span>
                                    <span className="zrl-panel-summary-value">{totalTopics}</span>
                                </div>
                                <div className="zrl-panel-summary-item">
                                    <span className="zrl-panel-summary-title">Total Duration</span>
                                    <span className="zrl-panel-summary-value">{totalScheduledMinutes} min</span>
                                </div>
                                <div className="zrl-panel-summary-item">
                                    <span className="zrl-panel-summary-title">Min Required</span>
                                    <span className="zrl-panel-summary-value">{minTimeRequired || 0} min</span>
                                </div>
                                <div className="zrl-panel-summary-item">
                                    <span className="zrl-panel-summary-title">Cycle Days</span>
                                    <span className="zrl-panel-summary-value">{durationDays || 0}</span>
                                </div>
                                <div className="zrl-panel-summary-item">
                                    <span className="zrl-panel-summary-title">Days Covered</span>
                                    <span className="zrl-panel-summary-value zrl-panel-summary-value-green">
                                        {daysCovered} / {durationDays || 0}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Category Progress */}
                        <div style={{ marginBottom: 16 }}>
                            <h4 className="zrl-panel-header">Category Progress</h4>
                            {categories.length === 0 ? (
                                <div style={{ fontSize: '0.82rem', color: 'var(--slate-gray)' }}>
                                    No categories yet.
                                </div>
                            ) : (
                                categories.map((cat) => {
                                    const scheduled = cat.topicRefs.reduce((s, t) => s + t.timeValue, 0);
                                    const target = cat.targetMinutes || 1;
                                    const pct = Math.min(100, Math.round((scheduled / target) * 100));
                                    return (
                                        <div key={cat.id} style={{ marginBottom: 10 }}>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    fontSize: '0.78rem',
                                                    marginBottom: 4,
                                                }}
                                            >
                                                <span style={{ color: 'var(--navy-blue)', fontWeight: 500 }}>
                                                    {cat.name || 'Unnamed'}
                                                </span>
                                                <span style={{ color: 'var(--slate-gray)' }}>
                                                    {scheduled}/{cat.targetMinutes} min
                                                </span>
                                            </div>
                                            <div
                                                style={{
                                                    height: 6,
                                                    background: '#e8ecef',
                                                    borderRadius: 3,
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        height: '100%',
                                                        width: `${pct}%`,
                                                        background: pct >= 100 ? '#1a8a4a' : 'var(--navy-blue)',
                                                        borderRadius: 3,
                                                        transition: 'width 0.2s',
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {/* Status Toggle */}
                        <div>
                            <h4 className="zrl-panel-header">Status</h4>
                            <div className="zrl-radio-group">
                                <label
                                    className={`zrl-radio-row ${status === 'DRAFT' ? 'selected' : ''}`}
                                    onClick={() => setStatus('DRAFT')}
                                >
                                    <input
                                        type="radio"
                                        name="plan-status"
                                        checked={status === 'DRAFT'}
                                        onChange={() => setStatus('DRAFT')}
                                    />
                                    <div>
                                        <div className="zrl-radio-label">Draft</div>
                                        <div className="zrl-radio-desc">Not assignable to companies yet</div>
                                    </div>
                                </label>
                                <label
                                    className={`zrl-radio-row ${status === 'ACTIVE' ? 'selected' : ''}`}
                                    onClick={() => setStatus('ACTIVE')}
                                >
                                    <input
                                        type="radio"
                                        name="plan-status"
                                        checked={status === 'ACTIVE'}
                                        onChange={() => setStatus('ACTIVE')}
                                    />
                                    <div>
                                        <div className="zrl-radio-label">Active</div>
                                        <div className="zrl-radio-desc">Available for assignment to companies</div>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Error */}
                {error && (
                    <div className="zrl-warning-banner" style={{ marginTop: 16 }}>
                        <span className="zrl-warning-icon">⚠️</span>
                        <span>{error}</span>
                    </div>
                )}
            </CustomModal>

            {/* Topic Pool Picker Modal */}
            {pickerCategory && (
                <TopicPoolPickerModal
                    isOpen={!!pickerCategoryId}
                    onClose={handlePickerClose}
                    categoryName={pickerCategory.name || 'Unnamed Category'}
                    alreadyAddedTopicIds={pickerCategory.topicRefs.map((t) => t.topicId)}
                    onAddTopics={handleTopicsAdded}
                />
            )}
        </>
    );
};
